/**
 * Route guard: validates the user has access to the property specified in the URL.
 *
 * Checks the :propertyId URL param against the user's property list from auth context.
 * - Loading → spinner
 * - No access → access-denied page with link back to properties
 * - Access → render children
 */

import type { ReactNode } from "react"
import { useParams, Link } from "react-router"
import { useAuth } from "@/lib/auth"

export function ValidatePropertyAccess({ children }: { children: ReactNode }) {
  const params = useParams<{ propertyId: string }>()
  const { properties, isLoading } = useAuth()

  if (isLoading) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100vh" }}>
        <div className="sf-spinner" />
      </div>
    )
  }

  const hasAccess = properties.some((p) => p.id === params.propertyId)

  if (!hasAccess) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100vh" }}>
        <div className="section-card" style={{ maxWidth: 440, textAlign: "center" }}>
          <h2 className="section-card__title">Property Not Found</h2>
          <p style={{ color: "var(--sf-text-muted)", margin: "var(--sf-space-4) 0" }}>
            You don't have access to this property, or it doesn't exist.
          </p>
          <Link
            to="/properties"
            style={{
              display: "inline-block",
              padding: "var(--sf-space-2) var(--sf-space-4)",
              background: "var(--sf-primary)",
              color: "white",
              borderRadius: "var(--sf-radius-sm)",
              textDecoration: "none",
              fontWeight: 500,
            }}
          >
            Back to Properties
          </Link>
        </div>
      </div>
    )
  }

  return <>{children}</>
}
